import React, { useCallback, useRef, useState } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { debounce, isEmpty } from 'lodash-es';
import pMap from 'p-map';
import { Virtuoso } from 'react-virtuoso';
import { faMagnifyingGlass } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import { gql } from '@soundxyz/gql-string';
import { styled } from '@components-shared/src/stitches';
import { Spinner } from '@components/basics/Spinner';
import { StyledScroll } from '@components/main-components/containers/RadixScroll';
import { NoPlaylistsFound } from '@components/main-components/media/illustrations/NoPlaylistsFound';
import { Modal } from '@components/main-components/modals/Modal';
import { ModalButton } from '@components/main-components/modals/ModalButton';
import { SearchInput, SearchInputContainer } from '@components/main-components/search/SearchInput';
import { useAuth } from '@context/auth/base';
import { MediaQuery } from '@context/mediaQuery';
import { ModalContext } from '@context/modal';
import { ToastContext } from '@context/toast';
import type { UserPlaylistsSearchQuery } from '@graphql/reactQuery';
import { useMutation, useQuery } from '@graphql/reactQuery';
import {
  AddReleaseToPlaylistDocument,
  ReleasePlaylistsForUserDocument,
  UserPlaylistsSearchDocument,
} from '@graphql/reactQuery';
import { mergeUniqLists } from '@utils/mergeUniqueLists';
import { NewPlaylistRow, PlaylistRow } from './PlaylistRow';

/*#__PURE__*/ gql(/* GraphQL */ `
  query UserPlaylistsSearch($id: UUID!, $after: String, $text: NonEmptyString) {
    user(id: $id) {
      id
      shelves(pagination: { first: 20, after: $after }, filter: { text: $text }) {
        pageInfo {
          hasNextPage
          endCursor
        }
        edges {
          node {
            id
            name
            ...AddToPlaylistRow
          }
        }
      }
    }
  }

  mutation AddReleaseToPlaylist($input: AddReleaseToShelfInput!) {
    addReleaseToShelf(input: $input) {
      __typename
      ... on MutationAddReleaseToShelfSuccess {
        data {
          id
        }
      }
    }
  }

  query ReleasePlaylistsForUser($releaseId: UUID!, $playlistIds: [UUID!]) {
    release(id: $releaseId) {
      id
      shelves(pagination: { first: 20 }, filter: { shelfIds: $playlistIds }) {
        edges {
          node {
            id
          }
        }
      }
    }
  }
`);

export type UserPlaylistEdges = NonNullable<UserPlaylistsSearchQuery['user']>['shelves']['edges'];

const SEARCH_DEBOUNCE = 200;

export const AddToPlaylistModal = ({ releaseId }: { releaseId: string }) => {
  const { authUser } = useAuth();
  const { closeCancelModal } = ModalContext.useContainer();
  const { tablet } = MediaQuery.useContainer();
  const [playlistsOwnedByUser, setPlaylistsOwnedByUser] = useState<UserPlaylistEdges>([]);
  const [selectedPlaylistIds, setSelectedPlaylistIds] = useState(new Set<string>());
  const [addedPlaylistIds, setAddedPlaylistIds] = useState(new Set<string>());
  const [searchQuery, setSearchQuery] = useState('');
  const { mutateAsync: addReleaseToPlaylist } = useMutation(AddReleaseToPlaylistDocument, {});
  const [submitInProgress, setSubmitInProgress] = useState(false);
  const { openToast } = ToastContext.useContainer();

  const debouncedSetQuery = useRef(
    debounce((query: string) => {
      setPlaylistsOwnedByUser([]);
      setSearchQuery(query);
    }, SEARCH_DEBOUNCE),
  );

  const [paginationAfter, setPaginationAfter] = useState<string | null>(null);

  const { data: playlistsData, isLoading: playlistsLoading } = useQuery(
    UserPlaylistsSearchDocument,
    {
      variables: !!authUser && {
        id: authUser.id,
        text: searchQuery || undefined,
        after: paginationAfter,
      },
      onSuccess(data) {
        setPlaylistsOwnedByUser(prev =>
          mergeUniqLists({
            current: prev,
            incoming: data.data.user?.shelves.edges,
            uniq({ node }) {
              return node.id;
            },
          }),
        );
      },
      staleTime: 0,
    },
  );

  const playlistDataEdges = playlistsData?.data.user?.shelves?.edges;
  const pageInfo = playlistsData?.data.user?.shelves.pageInfo;

  // When a new page of playlists is fetched, check to see if the current release is in any of them
  useQuery(ReleasePlaylistsForUserDocument, {
    variables: {
      releaseId,
      playlistIds: playlistDataEdges?.map(({ node: playlist }) => playlist.id) ?? [],
    },
    onSuccess: data => {
      setAddedPlaylistIds(prev => {
        const newSet = new Set(prev);
        data?.data.release?.shelves.edges.forEach(({ node: playlist }) => {
          newSet.add(playlist.id);
        });
        return newSet;
      });
    },
    enabled: !isEmpty(playlistDataEdges),
    staleTime: 0,
  });

  const loadMore = () => {
    if (!pageInfo?.hasNextPage) return;

    setPaginationAfter(pageInfo.endCursor || null);
  };

  const handleSubmit = async () => {
    if (!releaseId || selectedPlaylistIds.size === 0) return;

    setSubmitInProgress(true);
    try {
      let playlistErrorCount = 0;
      const successPlaylistIds: string[] = [];

      await pMap(
        Array.from(selectedPlaylistIds),
        async (id: string) => {
          const addReleaseData = await addReleaseToPlaylist({
            input: { releaseId, shelfId: id },
          });

          if (
            addReleaseData?.data?.addReleaseToShelf?.__typename ===
            'MutationAddReleaseToShelfSuccess'
          ) {
            successPlaylistIds.push(id);
          } else {
            playlistErrorCount++;
          }
        },
        { stopOnError: false, concurrency: 4 },
      );

      if (playlistErrorCount > 0) {
        openToast({
          text: `Error adding to ${playlistErrorCount} playlist${
            playlistErrorCount === 1 ? '' : 's'
          }`,
          variant: 'error',
        });
      }

      if (successPlaylistIds.length > 1) {
        openToast({
          text: (
            <>
              Added to <strong>{successPlaylistIds.length} playlists</strong>
            </>
          ),
          variant: 'success',
        });
        closeCancelModal();
      } else if (successPlaylistIds.length === 1) {
        const selectedPlaylist = playlistsOwnedByUser.find(
          ({ node: playlist }) => playlist.id === successPlaylistIds[0],
        );
        openToast({
          text: (
            <>
              Added to <strong>{selectedPlaylist?.node?.name}</strong>
            </>
          ),
          variant: 'success',
        });
        closeCancelModal();
      } else {
        setSubmitInProgress(false);
      }
    } catch (error) {
      setSubmitInProgress(false);
      openToast({ text: 'Error adding to playlist', variant: 'error' });
    }
  };

  const header = (
    <HeaderContainer>
      <h3>Add to playlist</h3>
      <p>Add to existing playlists or create a new playlist</p>
    </HeaderContainer>
  );

  const items = useCallback(
    (_: number, { node: playlist }: UserPlaylistEdges[number]) => {
      const playlistId = playlist.id;
      const isSelected = selectedPlaylistIds.has(playlistId);
      const isAlreadyAdded = addedPlaylistIds.has(playlist.id);

      const onSelect = () => {
        if (isAlreadyAdded) return;

        setSelectedPlaylistIds(prev => {
          const newSet = new Set(prev);
          if (isSelected) {
            newSet.delete(playlistId);
          } else {
            newSet.add(playlistId);
          }
          return newSet;
        });
      };

      return (
        <PlaylistRow
          playlistFragment={playlist}
          isSelected={isSelected}
          isAlreadyAdded={isAlreadyAdded}
          onSelect={onSelect}
        />
      );
    },
    [selectedPlaylistIds, addedPlaylistIds],
  );

  const RowFooter = useCallback(
    () =>
      playlistsLoading ? (
        <LoadingSpinner>
          <Spinner size="lg" />
        </LoadingSpinner>
      ) : null,
    [playlistsLoading],
  );

  const body = (
    <>
      <SearchInputContainer
        css={{
          borderRadius: '$sm',
          height: 50,
          padding: '11px 12px',
          background: '$neutral50',
          maxWidth: 'none',
        }}
      >
        <FontAwesomeIcon icon={faMagnifyingGlass} size="lg" />
        <SearchInput
          css={{ height: 35, margin: '0.1rem 0.5rem 0' }}
          type="text"
          placeholder="Search your playlists"
          onChange={event => debouncedSetQuery.current(event.target.value)}
        />
      </SearchInputContainer>
      {searchQuery.length === 0 && <NewPlaylistRow releaseId={releaseId} />}
      {playlistsLoading || playlistsOwnedByUser.length > 0 || searchQuery.length === 0 ? (
        <ListContainer>
          <Virtuoso
            style={{ height: 'calc(100% - 68px' }}
            data={playlistsOwnedByUser}
            itemContent={items}
            endReached={loadMore}
            fixedItemHeight={80}
            components={{ Footer: RowFooter }}
          />
        </ListContainer>
      ) : (
        <Empty>
          <NoPlaylistsFound />
          <p>No playlists found for “{searchQuery}”</p>
        </Empty>
      )}
    </>
  );

  const footer = (
    <FooterContainer>
      <ModalButton
        variant="secondary"
        label="Cancel"
        onClick={closeCancelModal}
        fullWidth={tablet ? false : true}
        deprecatedCSS={{
          minWidth: 167,
          '@tablet': {
            minWidth: 94,
          },
        }}
      />
      <ModalButton
        variant="primary"
        label="Add to Playlist"
        onClick={handleSubmit}
        fullWidth={tablet ? false : true}
        disabled={selectedPlaylistIds.size === 0}
        loading={submitInProgress}
        deprecatedCSS={{
          minWidth: 167,
          '@tablet': {
            minWidth: 136,
          },
        }}
      />
    </FooterContainer>
  );

  return (
    <Modal
      header={header}
      body={body}
      closeModal={closeCancelModal}
      footer={footer}
      bodyStyles={{ marginTop: 20, display: 'flex', flexDirection: 'column', height: '100%' }}
      containerStyles={{
        width: '100%',
        height: '100%',
        paddingBottom: 0,
        display: 'flex',
        flexDirection: 'column',
        '@tablet': { width: 600, height: 749, maxHeight: 'calc(100vh - 32px)' },
      }}
      footerStyles={{
        backgroundColor: '$white',
        height: 68,
        margin: 0,
        padding: '12px 16px',
        alignItems: 'center',
        position: 'fixed',
        bottom: 0,
        left: 0,
        boxShadow: '0px 0px 0px 1px rgba(0, 0, 0, 0.04), 0px 8px 28px rgba(0, 0, 0, 0.28)',
        '@tablet': { position: 'absolute', paddingX: 24 },
      }}
    />
  );
};

const HeaderContainer = styled('div', {
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  h3: {
    base: 'L',
    fontStyle: 'semibold',
  },
  p: {
    base: 'M',
    fontStyle: 'regular',
    color: '$neutral500',
  },

  '@tablet': {
    h3: {
      base: 'XL',
    },
    p: {
      base: 'L',
      color: '$neutral500',
    },
  },
});

const FooterContainer = styled('div', {
  display: 'flex',
  gap: 8,
  width: '100%',
  justifyContent: 'space-evenly',
  backgroundColor: '$white',
  '@tablet': {
    width: 'unset',
    gap: 16,
  },
});

const LoadingSpinner = styled('div', {
  height: 80,
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const ListContainer = styled('div', {
  height: '100%',
  '> div': {
    ...StyledScroll,
  },
});

const Empty = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  marginTop: 24,
  gap: 8,
  base: 'S',
  color: '$neutral900',
});
